import React, { useState } from 'react';

import { Input, Modal, Text, toastService } from '@thefarmersfront/kitchen';

import { InfoText } from '~/components/common';

import { usePatchUserModifyPassword } from '~/hooks/auth';
import { useLoadingStore, useUserInfoStore } from '~/store';
import {
  validateAuthPassword,
  validateConsecutiveKeyboardPattern,
  validateLetterRepeatThreeTimes,
  validateNoSpace,
} from '~/utils';

import { CHANGE_PASSWORD, CHANGE_PASSWORD_INFO_TEXT } from '~/constants';

import type { TypeErrorResponseData, TypeInputState, TypeInputStatus } from '~/types/common.types';

import styles from './VenderChangePasswordModal.module.scss';

type TypeVenderChangePasswordModalProps = {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

const VenderChangePasswordModal = ({ open, onCancel, onConfirm }: TypeVenderChangePasswordModalProps) => {
  const { patchUserModifyPassword } = usePatchUserModifyPassword();

  const { setIsLoading } = useLoadingStore();
  const { userInfo } = useUserInfoStore();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');

  const [currentPasswordState, setCurrentPasswordState] = useState<TypeInputState>({
    status: 'default',
  });
  const [newPasswordState, setNewPasswordState] = useState<TypeInputState>({ status: 'default' });
  const [newPasswordConfirmState, setNewPasswordConfirmState] = useState<TypeInputState>({
    status: 'default',
  });

  const setAllFieldsStatusAtOnce = (status: TypeInputStatus) => {
    setCurrentPasswordState({ status });
    setNewPasswordState({ status });
    setNewPasswordConfirmState({ status });
  };

  const patchChangePassword = async () => {
    try {
      setIsLoading(true);

      await patchUserModifyPassword({
        loginId: userInfo.loginId,
        currentPassword,
        newPassword,
      });

      toastService.addToast({
        message: '성공적으로 저장되었습니다.',
      });

      onConfirm();
    } catch (error) {
      const errorCode = (error as TypeErrorResponseData['error'])?.code;

      switch (errorCode) {
        case CHANGE_PASSWORD.WRONG_CURRENT_PASSWORD:
          setCurrentPasswordState({
            status: 'error',
            message: (error as TypeErrorResponseData['error'])?.message,
          });

          return;
        case CHANGE_PASSWORD.SAME_WITH_CURRENT_PASSWORD:
        case CHANGE_PASSWORD.SAME_WITH_PREVIOUS_PASSWORD:
        case CHANGE_PASSWORD.SAME_WITH_EMAIL:
          setNewPasswordState({ status: 'error' });
          setNewPasswordConfirmState({
            status: 'error',
            message: (error as TypeErrorResponseData['error'])?.message,
          });
          return;
        default:
          break;
      }

      toastService.addToast({
        type: 'error',
        message: (error as TypeErrorResponseData['error'])?.message ?? '다시 시도해 주세요.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const validateEmptyFields = () => {
    if (!currentPassword) {
      setCurrentPasswordState({
        status: 'error',
        message: '현재 비밀번호를 입력해 주세요.',
      });
    }

    if (!newPassword) {
      setNewPasswordState({
        status: 'error',
        message: '새로운 비밀번호를 입력해 주세요.',
      });
    }

    if (!newPasswordConfirm) {
      setNewPasswordConfirmState({
        status: 'error',
        message: '새로운 비밀번호를 재입력해 주세요.',
      });
    }
  };

  const handleConfirm = () => {
    setAllFieldsStatusAtOnce('default');

    const isAllInputFilled = currentPassword && newPassword && newPasswordConfirm;
    if (!isAllInputFilled) {
      validateEmptyFields();

      return;
    }

    const isNewPasswordInputSame = newPassword === newPasswordConfirm;
    if (!isNewPasswordInputSame) {
      setNewPasswordConfirmState({
        status: 'error',
        message: '변경 비밀번호가 일치하지 않습니다',
      });

      return;
    }

    const isValidPassword =
      validateNoSpace(newPassword) &&
      !validateLetterRepeatThreeTimes(newPassword) &&
      !validateConsecutiveKeyboardPattern(newPassword) &&
      validateAuthPassword(newPassword);

    if (!isValidPassword) {
      setNewPasswordState({ status: 'error' });
      setNewPasswordConfirmState({
        status: 'error',
        message: '변경할 수 없는 비밀번호입니다.',
      });

      return;
    }

    patchChangePassword();
  };

  return (
    <Modal
      open={open}
      title="비밀번호 변경"
      cancelText="취소"
      confirmText="변경"
      onCancel={onCancel}
      confirmButtonType="submit"
      onConfirm={handleConfirm}
    >
      <div className={styles['change-password-modal']}>
        <div className={styles['input-items']}>
          <label htmlFor="current-password" className={styles['input-item']}>
            <Text weight="semi-bold" size="large" className={styles['input-label']}>
              현재 비밀번호
            </Text>
            <Input
              id="current-password"
              placeholder="현재 비밀번호 입력"
              size="small"
              className={styles['input-area']}
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              status={currentPasswordState.status}
              validationMessage={currentPasswordState.message ?? ''}
              onFocus={() => setCurrentPasswordState({ status: 'default' })}
            />
          </label>
          <label htmlFor="new-password" className={styles['input-item']}>
            <Text weight="semi-bold" size="large" className={styles['input-label']}>
              변경 비밀번호
            </Text>
            <Input
              id="new-password"
              placeholder="새로운 비밀번호 입력"
              size="small"
              type="password"
              className={styles['input-area']}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              status={newPasswordState.status}
              validationMessage={newPasswordState.message ?? ''}
              onFocus={() => setNewPasswordState({ status: 'default' })}
            />
          </label>
          <label htmlFor="new-password-confirm" className={styles['input-item']}>
            <Text weight="semi-bold" size="large" className={styles['input-label']}>
              변경 비밀번호 확인
            </Text>
            <Input
              id="new-password-confirm"
              placeholder="새로운 비밀번호 재입력"
              size="small"
              type="password"
              className={styles['input-area']}
              value={newPasswordConfirm}
              onChange={(e) => setNewPasswordConfirm(e.target.value)}
              status={newPasswordConfirmState.status}
              validationMessage={newPasswordConfirmState.message ?? ''}
              onFocus={() => setNewPasswordConfirmState({ status: 'default' })}
            />
          </label>
        </div>
        <div className={styles['change-password-form-info']}>
          {CHANGE_PASSWORD_INFO_TEXT.map((text) => (
            <InfoText key={text}>{text}</InfoText>
          ))}
        </div>
      </div>
    </Modal>
  );
};

export default VenderChangePasswordModal;
